import { useState, useCallback } from 'react'
import { Link } from '@inertiajs/react'
import { Badge } from '@/components/catalyst/badge'
import { FormInput } from '@/components/ui/form'
import { getOrderStatusColor } from '@/hooks/useOrderStatus'
import type { DashboardColumn } from './columns'

// ─── Types ──────────────────────────────────────

export interface CellProps {
  value: unknown
  row: Record<string, unknown>
  column: DashboardColumn
  csrfToken: string
  onCellUpdate: (rowId: number, updates: { col: string; val: unknown }[]) => void
  onNotify: (msg: string) => void
  onFlash?: (rowId: number, status: 'success' | 'error') => void
  // Select options (passed from parent)
  statesOptions?: { label: string; value: string }[]
  subStateNames?: Record<string, Record<string, string>>
  subStateComments?: Record<string, Record<string, string>>
  probabilitiesOptions?: { label: string; value: string }[]
  financialStagesOptions?: { label: string; value: string }[]
  usersList?: { label: string; value: string }[]
  currentUserRole?: string
  isHovered?: boolean
}

// ─── Helpers ────────────────────────────────────

function humanize(s: string | null | undefined): string {
  if (!s) return '—'
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function formatCurrency(v: unknown): string {
  if (v == null || v === '') return '—'
  const n = typeof v === 'number' ? v : parseFloat(String(v))
  if (isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
}

function formatFloat(v: unknown): string {
  if (v == null || v === '') return '—'
  const n = typeof v === 'number' ? v : parseFloat(String(v))
  if (isNaN(n)) return String(v)
  return `${n.toFixed(1)}%`
}

function formatDate(v: unknown): string {
  if (!v) return '—'
  return String(v)
}

async function saveField(orderId: number, field: string, value: unknown, csrfToken: string): Promise<boolean> {
  const res = await fetch(`/inertia_customer_orders/${orderId}.json`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
    body: JSON.stringify({ customer_order: { [field]: value } }),
  })
  return res.ok
}

// ─── Auto-open select wrapper ───────────────────
// Clicks the Listbox button on mount so dropdown opens immediately.
// On blur (focus leaves wrapper) → calls onClose to revert cell to display mode.

// ─── Life Cell ──────────────────────────────────

function LifeCell({ value }: { value: unknown }) {
  const days = typeof value === 'number' ? value : parseInt(String(value || '0'))
  if (isNaN(days)) return <span className="text-zinc-400">—</span>

  const color = days < 7 ? 'green' : days < 30 ? 'yellow' : 'red'
  return <Badge color={color}>{days}d</Badge>
}

// ─── State Cell ─────────────────────────────────

function StateCell({ value }: CellProps) {
  const strVal = String(value || '')
  const currentKey = strVal.toLowerCase().replace(/ /g, '_')
  const color = getOrderStatusColor(currentKey)
  return <Badge color={color}>{humanize(strVal) || '—'}</Badge>
}

// ─── Select Cell (sub_state, probability, etc.) ─

function SelectCell({ value }: CellProps) {
  return <span className="truncate">{humanize(String(value || '')) || '—'}</span>
}

// ─── Boolean Cell ───────────────────────────────

function BooleanCell({ value, row, column, csrfToken, onCellUpdate, onNotify, currentUserRole }: CellProps) {
  const checked = value === 'YES' || value === true
  const [loading, setLoading] = useState(false)

  // disabled conditions from old code
  const disabled =
    ((column.name === 'inland' || column.name === 'freight_duties') && row.has_invoice === false) ||
    (column.name === 'approved' && currentUserRole !== 'Admin')

  const toggle = useCallback(async () => {
    if (disabled || loading) return
    setLoading(true)
    const newVal = !checked
    const ok = await saveField(row.id as number, column.name, newVal, csrfToken)
    if (ok) {
      onCellUpdate(row.id as number, [{ col: column.name, val: newVal ? 'YES' : 'NO' }])
      onNotify(`Saved:${humanize(column.name)} updated`)
    }
    setLoading(false)
  }, [checked, disabled, loading, row, column, csrfToken, onCellUpdate, onNotify])

  return (
    <button
      onClick={toggle}
      disabled={disabled}
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
        disabled ? 'cursor-not-allowed opacity-40' :
        checked ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400' :
        'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
      } ${loading ? 'animate-pulse' : ''}`}
    >
      {checked ? 'YES' : 'NO'}
    </button>
  )
}

// ─── Link Cell (po_number) ──────────────────────

function LinkCell({ value, row }: { value: unknown; row: Record<string, unknown> }) {
  if (!value) return <span className="text-zinc-400">—</span>
  return (
    <Link
      href={`/inertia_customer_orders/${row.id}`}
      className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
    >
      {String(value)}
    </Link>
  )
}

// ─── IDs Cell (parent_id, child_ids) ────────────

function IdsCell({ value }: { value: unknown }) {
  if (!value) return <span className="text-zinc-400">—</span>
  const ids = Array.isArray(value) ? value : [value]
  return (
    <div className="flex flex-wrap gap-1">
      {ids.map((id, i) => (
        <Link key={i} href={`/inertia_customer_orders/${id}`} className="text-indigo-600 hover:underline dark:text-indigo-400">
          #{id}
        </Link>
      ))}
    </div>
  )
}

// ─── Text Edit Cell ─────────────────────────────

function TextEditCell({ value, row, column, csrfToken, onCellUpdate, onNotify, onFlash }: CellProps) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(String(value || ''))
  const rowId = row.id as number

  const handleBlur = useCallback(async () => {
    setEditing(false)
    if (text === String(value || '')) return // skip unchanged
    try {
      const ok = await saveField(rowId, column.name, text, csrfToken)
      if (ok) {
        onCellUpdate(rowId, [{ col: column.name, val: text }])
        onFlash?.(rowId, 'success')
        onNotify(`Saved:${humanize(column.name)} updated`)
      } else {
        onFlash?.(rowId, 'error')
        onNotify(`error:Error:Failed to update ${humanize(column.name)}`)
      }
    } catch {
      onFlash?.(rowId, 'error')
      onNotify('error:Error:Network error')
    }
  }, [text, value, rowId, column, csrfToken, onCellUpdate, onNotify, onFlash])

  if (editing) {
    return (
      <FormInput
        autoFocus
        className="!py-0.5 !text-xs"
        value={text}
        onChange={e => setText((e.target as HTMLInputElement).value)}
        onBlur={handleBlur}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
      />
    )
  }

  return (
    <button onClick={() => setEditing(true)} className="w-full cursor-pointer truncate rounded px-1 py-0.5 text-left hover:bg-zinc-100 dark:hover:bg-zinc-800">
      {text || <span className="text-zinc-400">—</span>}
    </button>
  )
}

// ─── User Select Cell ───────────────────────────

function UserSelectCell({ value }: CellProps) {
  const strVal = String(value || '')
  return <span className="truncate">{strVal || <span className="text-zinc-400">—</span>}</span>
}

// ─── Array Cell ─────────────────────────────────

function ArrayCell({ value }: { value: unknown }) {
  if (!value) return <span className="text-zinc-400">—</span>
  const arr = Array.isArray(value) ? value : String(value).split(',')
  return <span className="truncate">{arr.join(', ')}</span>
}

// ─── Main Render Function ───────────────────────

export function renderCell(props: CellProps): React.ReactNode {
  const { value, column } = props

  switch (column.type) {
    case 'life':
      return <LifeCell value={value} />
    case 'state':
      return <StateCell {...props} />
    case 'select':
      return <SelectCell {...props} />
    case 'boolean':
      return <BooleanCell {...props} />
    case 'link':
      return <LinkCell value={value} row={props.row} />
    case 'ids':
      return <IdsCell value={value} />
    case 'text_edit':
      return <TextEditCell {...props} />
    case 'user_select':
      return <UserSelectCell {...props} />
    case 'user_comment':
      return <TextEditCell {...props} /> // same pattern as text_edit for now
    case 'currency':
      return <span>{formatCurrency(value)}</span>
    case 'float':
      return <span>{formatFloat(value)}</span>
    case 'date':
      if (column.editable) {
        return <TextEditCell {...props} /> // editable date uses text edit pattern
      }
      return <span>{formatDate(value)}</span>
    case 'array':
      return <ArrayCell value={value} />
    default:
      // string type — check bold
      if (column.bold) {
        return <span className="font-semibold">{value != null ? String(value) : '—'}</span>
      }
      return <span className="truncate">{value != null ? String(value) : '—'}</span>
  }
}

// ─── Summary helpers ────────────────────────────

export function computeSummary(rows: Record<string, unknown>[], columnName: string, type: string): string {
  if (type !== 'currency' && type !== 'float') return ''
  const values = rows.map(r => {
    const v = r[columnName]
    return typeof v === 'number' ? v : parseFloat(String(v || '0'))
  }).filter(n => !isNaN(n))
  if (values.length === 0) return '—'
  const sum = values.reduce((a, b) => a + b, 0)
  return type === 'currency' ? formatCurrency(sum) : `${(sum / values.length).toFixed(1)}%`
}
